/**
 * @fileoverview Shared field definitions for all V3 world element types.
 *
 * These fields are common across all world element types (Place, Set, Organization,
 * Object, Species, System, Event) and provide a consistent structure for tracking
 * how elements relate to and change throughout the story.
 *
 * Each field has three descriptions:
 * - schemaDescription: Used for Zod .describe() - general purpose
 * - initialStateDescription: Used in prompts for Chapter 0 (initial state)
 * - journeyDescription: Used in prompts for Chapters 1+ (story progression)
 */

// =============================================================================
// SHARED FIELD DEFINITIONS - COMMON TO ALL WORLD ELEMENT TYPES
// =============================================================================

/**
 * Shared fields that appear in all world element types.
 * These track the element's relationship to the plot and what has been revealed.
 */
export const SHARED_WORLD_ELEMENT_FIELDS = [
  {
    key: 'relevanceToPlot',
    label: 'Relevance to Plot',
    schemaDescription: "How this element affects or is affected by the story",
    initialStateDescription: "The element's planned role in the narrative.",
    journeyDescription: "How this element has influenced or been influenced by events so far.",
  },
  {
    key: 'currentState',
    label: 'Current State',
    schemaDescription: "The element's current condition or status",
    initialStateDescription: "The element's condition at the start of the story.",
    journeyDescription: "The element's condition at this point in the story.",
  },
  {
    key: 'knownInformation',
    label: 'Known Information',
    schemaDescription: "What has been revealed to the reader about this element",
    initialStateDescription: "What the reader knows about this element at the start.",
    journeyDescription: "What the reader has learned about this element so far.",
  },
] as const;

/**
 * The keys of shared fields, for filtering in type-specific forms.
 */
export const SHARED_WORLD_ELEMENT_FIELD_KEYS = SHARED_WORLD_ELEMENT_FIELDS.map(f => f.key);

/**
 * Type for a single field definition.
 */
export type WorldElementFieldDefinition = {
  key: string;
  label: string;
  schemaDescription: string;
  initialStateDescription: string;
  journeyDescription: string;
};
